import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ModalBody } from 'reactstrap';
import { colors } from 'shared/defaultTheme';
import { ModalHeader } from 'shared/components/modal/ModalHeaders';
import { useTranslation } from 'react-i18next';
import {
  setIsClientAccessManagerSelectorOpen,
  setClientAccessManagerAction,
  setClientAccessManagerContacts,
} from 'redux/actions/clientAccessManagerActions';
import ClientsContactSelector from 'shared/components/ClientsContactSelector';
import { Button } from 'shared/components/Buttons';
import useApi from 'hooks/useApi';
import { convertErrorsArrayToString } from 'shared/helpers/helper';
import { showAlert, closeAlert } from 'redux/actions/alertActions';
import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS } from 'shared/utils';
import MainSpinner from 'shared/components/MainSpinner';
import AlertModal from 'shared/components/AlertModal';
import useClient from 'hooks/useClient';
import { AccessManagerSelectorModal } from '../styles';

const ClientAccessManagerSelectorModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { customAreaApi } = useApi();

  const [selectedContacts, setSelectedContacts] = useState([]);
  const [contacts, setContacts] = useState([]);

  const { clientAccessManagerContacts, isClientAccessManagerSelectorOpen, action } = useSelector(
    (state) => state.clientAccessManager
  );
  const { openAlert, alertType, alertMessage } = useSelector((state) => state.alertState);

  const { getAllContacts, getAllContactsLoading } = useClient();

  const fetchAllContacts = async () => {
    const allContacts = await getAllContacts({
      filters: [
        {
          model: 'Contact',
          field: 'Name',
          asc: true,
        },
      ],
    });
    dispatch(setClientAccessManagerContacts(allContacts));
  };

  useEffect(() => {
    return () => {
      dispatch(setClientAccessManagerAction(''));
    };
  }, []);

  const [{ loading: loadingBulkAccessAction }, bulkAccessAction] = customAreaApi(
    { method: 'POST' },
    { manual: true }
  );

  const handleAction = async (body) => {
    try {
      const urlPaths = {
        add: `${window.REACT_APP_SERVER_URL}${window.API_V1}/login/access/massive`,
        resend: `${window.REACT_APP_SERVER_URL}${window.API_V1}/login/access/massive`,
        remove: `${window.REACT_APP_SERVER_URL}${window.API_V1}/login/access/revoke/massive`,
      };
      await bulkAccessAction({
        url: urlPaths[action],
        data: body,
      }).then(async (res) => {
        const {
          data: { errors },
          status,
        } = res;
        if (status === 200) {
          dispatch(
            showAlert({
              alertType: ALERT_TYPE_SUCCESS,
              alertMessage: t('general_success_message'),
            })
          );
        }
        if (errors) {
          throw new Error(convertErrorsArrayToString(errors));
        }
      });
    } catch (ex) {
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_ERROR,
          alertMessage: t('general_error_message'),
        })
      );
      console.error('error in bulk access action: ', ex);
    }
  };

  const onCloseModal = () => {
    dispatch(setIsClientAccessManagerSelectorOpen(false));
    setSelectedContacts([]);
  };

  const getModalTitle = () => {
    const modalTitles = {
      add: t('client_access_manager_add_title'),
      resend: t('client_access_manager_resend_title'),
      remove: t('client_access_manager_remove_title'),
    };
    return modalTitles[action] || '';
  };

  const getActionButtonLabel = () => {
    const actionButtonLabels = {
      add: t('client_access_manager_add_option'),
      resend: t('client_access_manager_resend_option'),
      remove: t('client_access_manager_remove_option'),
    };
    return actionButtonLabels[action] || '';
  };

  const getContactsFromSelectedContacts = () => {
    const contactsArray = [];
    Object.keys(selectedContacts).forEach((key) => {
      selectedContacts[key].forEach((contact) => {
        if (contact.isSelected) {
          contactsArray.push(contact);
        }
      });
    });
    return contactsArray;
  };

  const handleActionClick = async () => {
    const body = contacts.map((contact) => {
      return {
        login: contact.mail,
        idClient: contact.clientId,
        idCustomer: contact.clientNavision,
        domain: window.location.origin,
      };
    });
    await handleAction(body);
  };

  useEffect(() => {
    setContacts(getContactsFromSelectedContacts());
  }, [selectedContacts]);

  const onCloseAlert = async () => {
    await fetchAllContacts();
    dispatch(setIsClientAccessManagerSelectorOpen(false));
    dispatch(closeAlert());
  };

  if (!action) return null;

  return (
    <AccessManagerSelectorModal isOpen={isClientAccessManagerSelectorOpen} size="lg">
      <ModalHeader
        className="modal-header"
        backgroundColor={colors.transparent}
        onClose={onCloseModal}
      />
      <div className="title-wrapper">
        <h1 className="title">{getModalTitle()}</h1>
      </div>
      <ModalBody>
        <ClientsContactSelector
          contacts={clientAccessManagerContacts}
          selectedContacts={contacts}
          setSelectedContacts={setSelectedContacts}
        />
        <div className="d-flex justify-content-end w-100">
          <Button
            className="home__btn text-uppercase w-auto"
            type="button"
            label={getActionButtonLabel()}
            color="primary"
            onClick={handleActionClick}
            disabled={!contacts.length || contacts.length > 50}
          />
        </div>
      </ModalBody>
      {(loadingBulkAccessAction || getAllContactsLoading) && <MainSpinner />}
      <AlertModal
        type={alertType}
        isOpen={openAlert}
        toggle={onCloseAlert}
        message={alertMessage}
        okText={t('general_button_ok')}
        onOk={onCloseAlert}
      />
    </AccessManagerSelectorModal>
  );
};

export default ClientAccessManagerSelectorModal;
